/**
 * Enhanced error logging utility for detecting blank screen issues
 */

/**
 * Strip query strings and redact access_token values from a string.
 * Prevents API tokens in URLs from leaking into persisted error logs.
 */
function sanitizeString(str) {
  if (!str) return str;
  return String(str).replace(/([?&]access_token=)[^&\s]+/gi, '$1REDACTED');
}

class ErrorLogger {
  constructor() {
    this.errors = [];
    this.maxErrors = 100;
    this.setupGlobalHandlers();
  }

  setupGlobalHandlers() {
    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.logError('UnhandledPromiseRejection', event.reason, {
        promise: event.promise
      });
    });

    // Catch global errors
    window.addEventListener('error', (event) => {
      this.logError('GlobalError', event.error || event.message, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
    });

    // Detect blank content area
    this.startBlankScreenDetection();
  }

  logError(type, error, metadata = {}) {
    const errorEntry = {
      type,
      error: sanitizeString(error instanceof Error ? error.message : String(error)),
      stack: sanitizeString(error instanceof Error ? error.stack : null),
      metadata,
      timestamp: new Date().toISOString(),
      url: window.location.href.split('?')[0],
      userAgent: navigator.userAgent
    };

    this.errors.push(errorEntry);

    // Keep only last N errors
    if (this.errors.length > this.maxErrors) {
      this.errors.shift();
    }

    // Log to console with prefix for easy filtering
    console.warn('[ErrorLogger]', errorEntry);

    // Store in localStorage for persistence across reloads
    try {
      localStorage.setItem('errorLog', JSON.stringify(this.errors));
    } catch (e) {
      // Ignore quota exceeded errors
    }

    return errorEntry;
  }

  startBlankScreenDetection() {
    // To enable blank-screen detection, set localStorage key 'kegtron_debug_blank_screen' to '1'.
    // This is intentionally opt-in — the interval should not run in production for every user.
    const DEBUG_FLAG = 'kegtron_debug_blank_screen';
    if (typeof localStorage === 'undefined' || localStorage.getItem(DEBUG_FLAG) !== '1') return;

    this._blankScreenIntervalId = setInterval(() => {
      const contentElement = document.querySelector('.content');
      if (!contentElement) return;

      const contentText = contentElement.textContent || '';
      const hasVisibleContent = contentText.trim().length > 0;

      // Check if we're on a route that should have content
      const path = window.location.hash || window.location.pathname;
      const shouldHaveContent = path.includes('/sites') ||
                               path.includes('/reports') ||
                               path.includes('/account') ||
                               path.includes('/config');

      if (shouldHaveContent && !hasVisibleContent) {
        this.logError('BlankContentDetected', 'Content area is blank but should have content', {
          path,
          contentHTML: contentElement.innerHTML.substring(0, 500),
          timestamp: Date.now()
        });
      }
    }, 1000);
  }

  stopBlankScreenDetection() {
    if (this._blankScreenIntervalId !== undefined) {
      clearInterval(this._blankScreenIntervalId);
      this._blankScreenIntervalId = undefined;
    }
  }

  getErrors() {
    return this.errors;
  }

  clearErrors() {
    this.errors = [];
    try {
      localStorage.removeItem('errorLog');
    } catch (e) {
      // Ignore
    }
  }

  exportErrors() {
    return JSON.stringify(this.errors, null, 2);
  }
}

// Create singleton instance
const errorLogger = new ErrorLogger();

export default errorLogger;
